import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { FileText, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

export function AdminSubmissions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/admin/case-reviews").then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = filter === "all" ? rows : rows.filter((r) => r.payment_status === filter);

  return (
    <div data-testid="admin-submissions">
      <div className="eyebrow">Inbox</div>
      <h1 className="font-display text-4xl mt-3 text-[#0F172A]">Case Reviews</h1>

      <div className="mt-8 flex gap-3">
        {["all", "unpaid", "pending", "paid"].map((f) => (
          <button
            key={f}
            data-testid={`filter-${f}`}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 text-xs uppercase tracking-widest border ${filter === f ? "bg-[#0F172A] text-white border-[#0F172A]" : "border-[#0F172A]/25 text-[#0F172A]"}`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="mt-8 bg-white border border-[#0F172A]/10 divide-y divide-[#0F172A]/10">
        {loading && <div className="p-10 text-center text-[#4A4D50]">Loading…</div>}
        {!loading && filtered.length === 0 && <div className="p-10 text-center text-[#4A4D50]">No submissions.</div>}
        {filtered.map((r) => (
          <Link
            key={r.id}
            to={`/admin/submissions/${r.id}`}
            data-testid={`submission-${r.id}`}
            className="grid grid-cols-12 gap-4 p-5 hover:bg-[#F7F5F0] transition-colors"
          >
            <div className="col-span-3">
              <div className="text-sm font-medium text-[#0F172A]">{r.full_name}</div>
              <div className="text-xs text-[#4A4D50]">{r.email}</div>
            </div>
            <div className="col-span-2 text-sm text-[#4A4D50]">{r.visa_category}</div>
            <div className="col-span-3 text-sm text-[#4A4D50]">{r.field_of_expertise}</div>
            <div className="col-span-2 text-xs text-[#4A4D50] font-mono-plex">{new Date(r.created_at).toLocaleDateString()}</div>
            <div className="col-span-2 text-right">
              <span className={`text-xs px-3 py-1 border ${r.payment_status === "paid" ? "border-[#1A3626] text-[#1A3626]" : "border-[#C2A679] text-[#C2A679]"}`}>
                {r.payment_status}
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function AdminSubmissionDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [row, setRow] = useState(null);
  const [notes, setNotes] = useState("");

  const load = () => {
    api.get(`/admin/case-reviews/${id}`).then((r) => { setRow(r.data); setNotes(r.data.notes || ""); });
  };

  useEffect(load, [id]);

  const markPaid = async () => {
    await api.post(`/admin/case-reviews/${id}/mark-paid`);
    toast.success("Marked as paid");
    load();
  };

  const saveNotes = async () => {
    await api.put(`/admin/case-reviews/${id}/notes`, { notes });
    toast.success("Notes saved");
  };

  if (!row) return <div className="text-[#4A4D50]">Loading…</div>;

  return (
    <div data-testid="submission-detail">
      <button onClick={() => nav("/admin/submissions")} data-testid="back-to-list" className="text-sm text-[#4A4D50] flex items-center gap-2 hover:text-[#0F172A]">
        <ArrowLeft size={14} /> Back to inbox
      </button>
      <div className="mt-6 flex items-start justify-between flex-wrap gap-6">
        <div>
          <div className="eyebrow">{row.visa_category}</div>
          <h1 className="font-display text-4xl mt-3 text-[#0F172A]">{row.full_name}</h1>
          <div className="mt-2 text-[#4A4D50]">{row.email} · {row.phone || "—"}</div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs px-3 py-2 border ${row.payment_status === "paid" ? "border-[#1A3626] text-[#1A3626]" : "border-[#C2A679] text-[#C2A679]"}`}>
            {row.payment_status}
          </span>
          {row.payment_status !== "paid" && (
            <button onClick={markPaid} data-testid="mark-paid" className="btn-primary text-sm !py-3 !px-6">
              <CheckCircle2 size={16} /> Mark paid
            </button>
          )}
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-8">
        <Detail label="Citizenship" value={row.country_of_citizenship} />
        <Detail label="Current residence" value={row.current_country} />
        <Detail label="Field" value={row.field_of_expertise} />
        <Detail label="Highest degree" value={row.highest_degree || "—"} />
        <Detail label="Years experience" value={row.years_experience ?? "—"} />
        <Detail label="Publications" value={row.publications_count ?? "—"} />
        <Detail label="Citations" value={row.citations_count ?? "—"} />
        <Detail label="Timeline" value={row.timeline || "—"} />
        <Detail label="Submitted" value={new Date(row.created_at).toLocaleString()} />
      </div>

      <div className="mt-10">
        <div className="eyebrow">Case summary</div>
        <p className="mt-3 text-[#1A1D20] leading-relaxed whitespace-pre-wrap">{row.case_summary}</p>
      </div>
      {row.awards && (
        <div className="mt-8">
          <div className="eyebrow">Awards</div>
          <p className="mt-3 text-[#1A1D20] leading-relaxed whitespace-pre-wrap">{row.awards}</p>
        </div>
      )}

      <div className="mt-10">
        <div className="eyebrow">Documents ({row.files?.length || 0})</div>
        <ul className="mt-4 border border-[#0F172A]/10 bg-white divide-y divide-[#0F172A]/10">
          {(row.files || []).map((f) => (
            <li key={f.file_id} className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <FileText size={18} className="text-[#0F172A]" strokeWidth={1.5} />
                <div>
                  <div className="text-sm text-[#0F172A]">{f.original_filename}</div>
                  <div className="text-xs text-[#4A4D50] font-mono-plex">{(f.size / 1024).toFixed(1)} KB</div>
                </div>
              </div>
              <a
                href={`${API}/admin/files/${f.storage_path}`}
                data-testid={`download-${f.file_id}`}
                className="text-sm link-underline text-[#0F172A]"
                target="_blank"
                rel="noreferrer"
              >
                Download
              </a>
            </li>
          ))}
          {(row.files || []).length === 0 && <li className="p-6 text-center text-[#4A4D50]">No files uploaded.</li>}
        </ul>
      </div>

      <div className="mt-10">
        <div className="eyebrow">Attorney notes (private)</div>
        <Textarea
          data-testid="notes-input"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={6}
          className="mt-3 editorial-input !border !border-[#0F172A]/15 !rounded-none !p-4"
        />
        <button onClick={saveNotes} data-testid="save-notes" className="mt-4 btn-primary text-sm !py-3 !px-6">
          Save notes
        </button>
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="eyebrow">{label}</div>
      <div className="mt-2 text-[#0F172A]">{value}</div>
    </div>
  );
}
